import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Edit, Check, X } from 'lucide-react';

interface ClientActionBarProps {
  clientId: string;
  isEditMode: boolean;
  hasUnsavedChanges: boolean;
  isSavingChanges: boolean;
  onEnterEditMode: () => void;
  onExitEditMode: () => void;
  onSaveChanges: () => void;
  onCreateMealPlan: () => void;
  onDeleteClient?: () => void;
}

const shouldKeepClientsStatusParam = (status: string | null): boolean =>
  status === 'all' || status === 'active' || status === 'suspended' || status === 'draft' || status === 'pending_deletion';

export const ClientActionBar: React.FC<ClientActionBarProps> = ({
  clientId,
  isEditMode,
  hasUnsavedChanges,
  isSavingChanges,
  onEnterEditMode,
  onExitEditMode,
  onSaveChanges,
  onCreateMealPlan,
  onDeleteClient,
}) => {
  const [searchParams] = useSearchParams();
  
  // Get the page, limit, and status from URL params to preserve list state
  const getBackUrl = () => {
    const fromPage = searchParams.get('fromPage') || '1';
    const fromLimit = searchParams.get('fromLimit') || '10';
    const fromStatus = searchParams.get('fromStatus');
    let url = `/clients?page=${fromPage}&limit=${fromLimit}`;
    if (shouldKeepClientsStatusParam(fromStatus)) url += `&status=${fromStatus}`;
    return url;
  };

  const getDetailsV2Url = () => {
    const query = searchParams.toString();
    return query ? `/clients/${clientId}/details-v2/overview?${query}` : `/clients/${clientId}/details-v2/overview`;
  };
  
  return (
    <div className="w-full bg-gradient-to-r from-primary/5 via-primary/3 to-background rounded-2xl p-4 lg:p-6 border border-primary/10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        {/* Left side - Back button and client name */}
        <div className="flex items-center gap-4">
          <Link to={getBackUrl()}>
            <Button variant="ghost" size="sm" className="gap-2 font-manrope font-medium text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to Clients
            </Button>
          </Link>
        </div>

        {/* Right side - Action buttons */}
        <div className="flex w-full lg:w-auto flex-wrap items-center justify-start lg:justify-end gap-3">
          <Link to={getDetailsV2Url()}>
            <Button
              variant="outline"
              size="sm"
              className="min-h-11 text-sm font-manrope font-medium border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300"
            >
              New UI
            </Button>
          </Link>
          {/* Edit/Save/Cancel buttons */}
          <div className="flex items-center gap-2">
            {!isEditMode ? (
              <Button
                variant="ghost"
                onClick={onEnterEditMode}
                size="sm"
                className="h-9 w-9 p-0 hover:bg-primary/10 transition-colors"
              >
                <Edit className="h-4 w-4" />
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    console.log('Save button clicked, isSavingChanges:', isSavingChanges, 'hasUnsavedChanges:', hasUnsavedChanges);
                    onSaveChanges();
                  }}
                  disabled={!hasUnsavedChanges || isSavingChanges}
                  size="sm"
                  className="min-h-11 bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {isSavingChanges ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Save
                    </>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={onExitEditMode}
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-destructive/10 text-destructive hover:text-destructive transition-colors"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="hidden lg:block w-px h-6 bg-border/50"></div>

          {/* Main action buttons */}
          <div className="flex items-center gap-3">
            <Link to={`/clients/${clientId}/nutritional-targets`}>
              <Button variant="outline" size="sm" className="min-h-11 text-sm font-manrope font-medium border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300">
                Nutritional Targets
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="min-h-11 text-sm font-manrope font-medium border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300"
              onClick={onCreateMealPlan}
            >
              Create Meal Plan
            </Button>
            {onDeleteClient ? (
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 text-sm font-manrope font-medium border-destructive/30 text-destructive hover:bg-destructive/10 transition-all duration-300"
                onClick={onDeleteClient}
              >
                Delete
              </Button>
            ) : null}
            <Button variant="outline" size="sm" className="min-h-11 text-sm font-manrope font-medium border-primary/20 hover:border-primary hover:bg-primary/5 transition-all duration-300">
              Generate Invoice
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
